/**
 * /attendance/import — punch-log CSV import, dry-run → commit (ADR-0014 Phase 4
 * PR-3). Mirrors employeeImport.tsx. The punch-log importer is single-endpoint:
 * the dry-run (commit=false) returns the preview inline, and the SAME file is
 * re-posted with commit=true to commit (no server-side stored dry-run). The
 * biometric boundary (D-7) returns 422 INVALID_PUNCH_LOG, surfaced as an error.
 */
import { createRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { authedLayoutRoute } from './authed.js';
import { useAuth } from '../auth/AuthContext.js';
import { importPunchLog, type PunchLogResult } from '../api/attendance.js';
import { ApiError } from '../api/client.js';
import { Alert } from '../components/Alert.js';

export const attendanceImportRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/attendance/import',
  component: AttendanceImportPage,
});

function AttendanceImportPage(): JSX.Element {
  const { user } = useAuth();
  const isHr = user?.role === 'owner' || user?.role === 'admin' || user?.role === 'hr';
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [summary, setSummary] = useState<PunchLogResult | null>(null);
  const [committedMsg, setCommittedMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const dryRun = useMutation({
    mutationFn: () => importPunchLog(file!, false),
    onSuccess: (s) => { setErr(null); setCommittedMsg(null); setSummary(s); },
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'Validation failed.'),
  });
  const commit = useMutation({
    mutationFn: () => importPunchLog(file!, true),
    onSuccess: async (s) => {
      setErr(null);
      setSummary(null);
      setFile(null);
      setCommittedMsg(`Imported ${s.imported ?? 0} punch(es); ${s.skippedDuplicates ?? 0} duplicate(s) skipped.`);
      await queryClient.invalidateQueries({ queryKey: ['attendance', 'timesheet-periods'] });
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'Import failed; no punches were imported.'),
  });

  if (!isHr) {
    return (
      <section className="p-8 max-w-3xl">
        <Alert variant="error">Restricted to HR / admin / owner roles.</Alert>
      </section>
    );
  }

  return (
    <section className="p-8 space-y-6 max-w-3xl">
      <header className="space-y-2">
        <h1 className="font-serif text-3xl">Punch-log import</h1>
        <p className="text-sm text-gray-400">
          Upload a biometric terminal&rsquo;s punch log (deviceId, externalUserId, timestamp, direction). Devices are
          mapped to employees under Attendance config. Biometric templates/images are rejected — punch logs only.
        </p>
      </header>

      {committedMsg ? (
        <div role="status" className="rounded border border-green-900 bg-green-950/40 px-3 py-2 text-sm text-green-300">{committedMsg}</div>
      ) : null}

      <form
        className="rounded border border-gray-900 p-5 space-y-4"
        onSubmit={(e) => { e.preventDefault(); if (file) dryRun.mutate(); }}
      >
        <input
          type="file"
          accept=".csv"
          aria-label="Punch-log CSV"
          onChange={(e) => { setFile(e.target.files?.[0] ?? null); setSummary(null); setCommittedMsg(null); setErr(null); }}
          className="block text-sm text-gray-300"
        />
        <button type="submit" disabled={!file || dryRun.isPending} className="rounded bg-white text-black px-4 py-2 text-sm font-medium hover:bg-gray-200 disabled:opacity-50">
          {dryRun.isPending ? 'Validating…' : 'Upload & validate'}
        </button>
      </form>

      {err ? <Alert variant="error">{err}</Alert> : null}

      {summary !== null ? (
        <div className="space-y-5">
          <div className="flex gap-2 text-xs">
            <span className="rounded px-2 py-1 border border-green-800 text-green-400">{summary.wouldImport ?? 0} will import</span>
            <span className="rounded px-2 py-1 border border-yellow-800 text-yellow-400">{summary.unmapped.length} unmapped</span>
            <span className="rounded px-2 py-1 border border-red-800 text-red-400">{summary.rowErrors.length} row errors</span>
          </div>

          {summary.unmapped.length > 0 ? (
            <div className="rounded border border-yellow-900/60 bg-yellow-950/20 p-3 space-y-2">
              <span className="text-sm text-yellow-300">{summary.unmapped.length} punch(es) with no device→employee mapping (won&rsquo;t import)</span>
              <ul className="max-h-40 overflow-y-auto text-xs text-gray-300 space-y-1">
                {summary.unmapped.map((u, i) => (
                  <li key={i}><span className="text-gray-500">row {u.row}</span> · device {u.deviceId} / user {u.externalUserId}</li>
                ))}
              </ul>
            </div>
          ) : null}

          {summary.rowErrors.length > 0 ? (
            <div className="rounded border border-red-900/60 bg-red-950/20 p-3 space-y-2">
              <span className="text-sm text-red-300">{summary.rowErrors.length} row(s) with errors (skipped)</span>
              <ul className="max-h-40 overflow-y-auto text-xs text-gray-300 space-y-1">
                {summary.rowErrors.map((e, i) => (
                  <li key={i}><span className="text-gray-500">row {e.row}</span> · {e.message}</li>
                ))}
              </ul>
            </div>
          ) : null}

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => commit.mutate()}
              disabled={(summary.wouldImport ?? 0) === 0 || commit.isPending}
              className="rounded bg-white text-black px-4 py-2 text-sm font-medium hover:bg-gray-200 disabled:opacity-50"
            >
              {commit.isPending ? 'Importing…' : `Import ${summary.wouldImport ?? 0} punch(es)`}
            </button>
            <button type="button" onClick={() => { setSummary(null); setFile(null); }} className="rounded border border-gray-700 px-4 py-2 text-sm text-gray-300 hover:bg-gray-900">
              Cancel
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
